import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { transliterate, translateAsync, translateBatchAsync, Script } from '../utils/transliterate';
import { lookupTranslation } from '../i18n/translations';

// Re-export Script type for consumers
export type { Script } from '../utils/transliterate';

interface ScriptContextType {
  script: Script;
  setScript: (s: Script) => void;
  /** Translation function. UI strings resolve instantly from the dictionary;
   *  dynamic (admin) content is auto-translated via DeepL in the background. */
  t: (text: string) => string;
  /** Explicit async translation — returns a promise. */
  ta: (text: string) => Promise<string>;
  /** True while one or more DeepL translations are in flight. Lets the UI keep
   *  content dimmed until the new language's text has resolved (see useLangFade). */
  isTranslating: boolean;
}

const STORAGE_KEY = 'gradac-lang';

const ScriptContext = createContext<ScriptContextType>({
  script: 'cyr',
  setScript: () => {},
  t: (text) => text,
  ta: async (text) => text,
  isTranslating: false,
});

export const useScript = () => useContext(ScriptContext);

export const ScriptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [script, setScriptState] = useState<Script>(() => {
    // Read saved language on mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && ['cyr', 'lat', 'en', 'de', 'fr', 'it', 'el'].includes(saved)) {
        return saved as Script;
      }
    } catch {
      // localStorage unavailable
    }
    return 'cyr';
  });

  // --- Async translation machinery ---
  // React-level cache for translations received via DeepL / localStorage.
  // Keys are language-namespaced (`${lang}:${text}`), so entries from every
  // language can safely coexist — we never wipe this on language change.
  const [asyncCache, setAsyncCache] = useState<Record<string, string>>({});
  // Tracks keys already queued so we don't duplicate requests.
  const pendingRef = useRef<Set<string>>(new Set());
  // Texts queued during the current render cycle, flushed in useEffect.
  const queueRef = useRef<{ text: string; lang: string }[]>([]);
  // True while DeepL batches are in flight.
  const [translating, setTranslating] = useState(false);
  const inFlightRef = useRef(0);
  // Last resolved rendering for each source text, keyed by the original text
  // (NOT language-namespaced). While a new language's translation is still in
  // flight we render this instead of a raw Latin fallback, so the user keeps
  // seeing readable, already-resolved text.
  const lastResolvedRef = useRef<Record<string, string>>({});
  // Timer for micro-delay batching
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist language choice
  const setScript = useCallback((s: Script) => {
    setScriptState(s);
    try {
      localStorage.setItem(STORAGE_KEY, s);
    } catch {
      // localStorage unavailable
    }
  }, []);

  // Set html lang attribute for accessibility/SEO
  useEffect(() => {
    const langMap: Record<Script, string> = {
      cyr: 'sr-Cyrl', lat: 'sr-Latn',
      en: 'en', de: 'de', fr: 'fr', it: 'it', el: 'el',
    };
    document.documentElement.lang = langMap[script] || 'sr';
  }, [script]);

  // ⭐ KEY FIX: Reset all async translation state when language changes.
  // Without this, stale translations from the PREVIOUS language remain
  // visible via lastResolvedRef, and pendingRef blocks re-queueing texts
  // for the new language. This caused: switching EN → DE would show EN text
  // for any dynamic content not in the dictionary.
  const prevScriptRef = useRef<Script>(script);
  useEffect(() => {
    if (prevScriptRef.current === script) return;
    prevScriptRef.current = script;

    // 1. Clear the "show last resolved" cache — prevents showing old-language text
    lastResolvedRef.current = {};
    // 2. Clear pending set — allows all texts to be re-queued for new language
    pendingRef.current.clear();
    // 3. Clear queue — old language items are no longer relevant
    queueRef.current = [];
    // 4. Cancel any pending batch timer for old language
    if (batchTimerRef.current) {
      clearTimeout(batchTimerRef.current);
      batchTimerRef.current = null;
    }
    // 5. Clear asyncCache — forces re-lookup for new language
    //    (localStorage and server DB still have cached translations, so
    //     most texts resolve instantly without hitting DeepL)
    setAsyncCache({});
  }, [script]);

  // Flush function: collects all queued texts and sends them in one batch API call.
  const flushQueue = useCallback(() => {
    batchTimerRef.current = null;
    const items = queueRef.current.splice(0);
    if (items.length === 0) return;

    inFlightRef.current += 1;
    setTranslating(true);

    // Group by language (in practice always the same, but be safe)
    const byLang: Record<string, { text: string; key: string }[]> = {};
    for (const { text, lang } of items) {
      const key = `${lang}:${text}`;
      if (!byLang[lang]) byLang[lang] = [];
      byLang[lang].push({ text, key });
    }

    const promises: Promise<void>[] = [];
    const langs = Object.keys(byLang);

    for (let li = 0; li < langs.length; li++) {
      const lang = langs[li];
      const entries = byLang[lang];
      const texts = entries.map(e => e.text);
      const p = translateBatchAsync(texts, lang as Script)
        .then(resultMap => {
          const updates: Record<string, string> = {};
          for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const translated = resultMap.get(entry.text);
            if (translated) {
              updates[entry.key] = translated;
            }
            pendingRef.current.delete(entry.key);
          }
          if (Object.keys(updates).length > 0) {
            setAsyncCache(prev => ({ ...prev, ...updates }));
          }
        })
        .catch(() => {
          // On failure, clear pending flags so they can be retried
          for (let i = 0; i < entries.length; i++) {
            pendingRef.current.delete(entries[i].key);
          }
        });
      promises.push(p);
    }

    Promise.all(promises).then(() => {
      inFlightRef.current -= 1;
      if (inFlightRef.current === 0) setTranslating(false);
    });
  }, []);

  // Flush the translation queue after each render, with a micro-delay to
  // collect all texts from the current render cycle into one batch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (queueRef.current.length === 0) return;

    // Debounce: wait 60ms to collect all t() calls from this render +
    // any child renders before sending the batch.
    if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
    batchTimerRef.current = setTimeout(flushQueue, 60);
  });

  // Cleanup batch timer on unmount
  useEffect(() => {
    return () => {
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
    };
  }, []);

  const t = useCallback((text: string): string => {
    if (!text || !text.trim()) return text;

    // `cyr` and `lat` resolve synchronously and exactly — they can never be in
    // a "still loading" state, so we record + return them immediately.
    if (script === 'cyr') {
      lastResolvedRef.current[text] = text;
      return text;
    }
    if (script === 'lat') {
      const resolved = transliterate(text, 'lat');
      lastResolvedRef.current[text] = resolved;
      return resolved;
    }

    // --- Foreign language ---

    // 1. Dictionary lookup (instant, ~100+ UI strings)
    const dict = lookupTranslation(text, script);
    if (dict) {
      lastResolvedRef.current[text] = dict;
      return dict;
    }

    const cacheKey = `${script}:${text}`;

    // 2. React async cache (populated by batch flush after DeepL response)
    if (asyncCache[cacheKey]) {
      lastResolvedRef.current[text] = asyncCache[cacheKey];
      return asyncCache[cacheKey];
    }

    // 3. localStorage cache (synchronous — avoids flash of Latin for repeat visits)
    try {
      const lsVal = localStorage.getItem(`tr:${script}:${text}`);
      if (lsVal) {
        lastResolvedRef.current[text] = lsVal;
        return lsVal;
      }
    } catch {
      // localStorage unavailable
    }

    // 4. Queue for async batch DeepL translation (processed by flushQueue)
    if (!pendingRef.current.has(cacheKey)) {
      pendingRef.current.add(cacheKey);
      queueRef.current.push({ text, lang: script });
    }

    // 5. Not yet resolved for this language. Keep showing the last resolved
    //    rendering of this same source text to avoid layout jumps.
    return lastResolvedRef.current[text] ?? transliterate(text, 'lat');
  }, [script, asyncCache]);

  const ta = useCallback(
    (text: string) => translateAsync(text, script),
    [script]
  );

  return (
    <ScriptContext.Provider value={{ script, setScript, t, ta, isTranslating: translating }}>
      {children}
    </ScriptContext.Provider>
  );
};
